import { useEffect, useMemo, useRef, useState } from "react";
import { fetchProfileById } from "../../redux/profileSlice";
import { server } from "../../server";
import { getUserIdFromToken } from "../../utils/auth";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchShortlist,
  addShortlist,
  removeShortlist,
} from "../../redux/shortlistSlice";
import { fetchRequests } from "../../redux/requestsSlice";
import { fetchActivityData } from "../../redux/activitySlice";
import { fetchMyProfile } from "../../redux/myProfileSlice";
import axios from "axios";
import Header from "./Header";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Right from "./Right";

function Profile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const profileId = location.state?.profileId;
  const [screenLoading, setScreenLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestLoading, setRequestLoading] = useState({});
  const [shortlistLoadingIds, setShortlistLoadingIds] = useState([]);
  const shortlisted = useSelector((state) =>
    Array.isArray(state?.shortlist?.shortlists)
      ? state.shortlist.shortlists
      : [],
  );

  const {
    sentRequests = [],
    receivedRequests = [],
    loading: requestsLoading = false,
    error: requestsError = null,
  } = useSelector((state) => state?.requests || {});

  const { acceptedRequests } = useSelector((state) => state.activity);
  const { profile, error } = useSelector((state) => state.myProfile);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [visitLoading, setVisitLoading] = useState(true);
  const scrollRef = useRef(null);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);
  const [canVisit, setCanVisit] = useState(false);
  const [alreadyVisited, setAlreadyVisited] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchMyProfile());
  }, []);

  useEffect(() => {
    if (!visitLoading && !loading && profile && profileData) {
      setScreenLoading(false);
    }
  }, [visitLoading, loading, profile, profileData]);

  const handleUnlockProfile = async () => {
    if (unlockLoading) return;

    try {
      setUnlockLoading(true);
      const token = localStorage.getItem("authToken");

      if (!token || !userId || !profileId) return;

      const response = await axios.post(
        `${server}/unlockedProfile/UnlockProfile`,
        { userId, unlockedProfileId: profileId },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        },
      );

      // ✅ Only unlock if backend confirms success
      if (response.status === 200 || response.data?.success === true) {
        setIsUnlocked(true);
      } else {
        alert("Could not unlock profile. Please try again.");
      }
    } catch (error) {
      const upgrade = window.confirm(
        "No active membership plan.\n\nDo you want to upgrade now?",
      );
      if (upgrade) navigate("/upgrade");
    } finally {
      setUnlockLoading(false);
    }
  };
  
  useEffect(() => {
    const checkProfile = async () => {
      setVisitLoading(true);
      try {
        const token = localStorage.getItem("authToken");

        if (!token) return setVisitLoading(false);
        if (!userId || !profileId || userId === profileId)
          return setVisitLoading(false);

        const url = `${server}/profilevisit/CheckVisitProfileLimit?userId=${userId}&profileToVisitId=${profileId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) return setVisitLoading(false);

        const data = await response.json();
        console.log("Check Profile Response:", data);

        setCanVisit(data.canVisit);
        setAlreadyVisited(data.alreadyVisited);
      } catch (error) {
        console.error("Check profile API failed:", error);
      } finally {
        setVisitLoading(false);
      }
    };

    checkProfile();
  }, [userId, profileId]);

  useEffect(() => {
    const visitProfile = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;
        if (!userId || !profileId || userId === profileId) return;

        await fetch(`${server}/profilevisit/VisitProfile`, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: userId,
            visitedProfileId: profileId,
          }),
        });
      } catch (error) {
        console.error("Visit profile API failed:", error);
      }
    };

    if (canVisit === true && alreadyVisited === false) {
      visitProfile();
    }
  }, [canVisit, alreadyVisited, userId, profileId]);

  // Add a new loading state at the top with your other states
  const [unlockCheckLoading, setUnlockCheckLoading] = useState(true);

  // Replace your existing checkProfile useEffect for unlock with this:
  useEffect(() => {
    const checkUnlockStatus = async () => {
      // ✅ Guard: only run when both IDs are valid and different
      if (!userId || !profileId || userId === profileId) {
        setUnlockCheckLoading(false);
        return;
      }

      try {
        setUnlockCheckLoading(true);
        const token = localStorage.getItem("authToken");

        if (!token) {
          setIsUnlocked(false);
          return;
        }

        const response = await fetch(
          `${server}/unlockedProfile/CheckUnlockedProfile`,
          {
            method: "POST",
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              userId: userId,
              checkProfileId: profileId,
            }),
          },
        );

        if (!response.ok) {
          setIsUnlocked(false);
          return;
        }

        const data = await response.json();

        // ✅ Strict check — only true if explicitly true
        setIsUnlocked(data?.unlocked === true);
      } catch (error) {
        console.error("Unlock check API Error:", error);
        setIsUnlocked(false); // ✅ Default to locked on any error
      } finally {
        setUnlockCheckLoading(false);
      }
    };

    checkUnlockStatus();
  }, [userId, profileId]);

  const getMaskedMobile = (mobile) => {
    if (!mobile) return null;
    return `${mobile.toString().slice(0, 4)}******`;
  };

  const getMaskedEmail = (email) => {
    if (!email || !email.includes("@")) return "Not Mentioned";
    const atIndex = email.indexOf("@");
    return `${"*".repeat(atIndex)}${email.slice(atIndex)}`;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchProfile = async () => {
      setLoading(true);
      try {
        const action = await dispatch(fetchProfileById(profileId));
        if (isMounted && action.payload) {
          setProfileData(action.payload);
        }
      } catch (err) {
        console.error("Profile fetch failed:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (profileId) fetchProfile();

    return () => {
      isMounted = false;
    };
  }, [profileId, dispatch]);

  const getAge = (dob) =>
    Math.floor((Date.now() - dob) / (365.25 * 24 * 60 * 60 * 1000));

  const parseHeight = (heightStr) => {
    const match = heightStr.match(/(\d+)ft\s+(\d+)in/);
    return match ? parseInt(match[1]) * 12 + parseInt(match[2]) : 0;
  };

  const generatePreferences = (myProfile, otherProfile) => {
    const myPrefs = myProfile.preferences;
    const theirData = otherProfile.profile;

    const theirAge = getAge(theirData.dob);
    const theirHeight = parseHeight(theirData.height);

    const inchesToFeetInches = (inches) => {
      if (!inches) return "";
      const feet = Math.floor(inches / 12);
      const inch = inches % 12;
      return `${feet}'${inch}"`;
    };

    const matchField = (key) => {
      const myPref = myPrefs[key];
      const theirValue = theirData[key];
      return myPref?.includes("Open to All") || myPref?.includes(theirValue);
    };

    const formatPrefValue = (prefValue) => {
      if (!prefValue) return "";
      if (Array.isArray(prefValue)) {
        if (prefValue.length <= 1) return prefValue.join(", ");
        const firstTwo = prefValue.slice(0, 1).join(", ");
        const moreCount = prefValue.length - 1;
        return `${firstTwo} +${moreCount} More`;
      }
      return prefValue;
    };

    return [
      {
        label: "Age",
        value: `${myPrefs.minAge} to ${myPrefs.maxAge}`,
        matched: theirAge >= myPrefs.minAge && theirAge <= myPrefs.maxAge,
      },
      {
        label: "Height",
        value: `${inchesToFeetInches(
          myPrefs.minHeight,
        )} to ${inchesToFeetInches(myPrefs.maxHeight)}`,
        matched:
          (myPrefs.minHeight === 0 && myPrefs.maxHeight === 0) ||
          (theirHeight >= myPrefs.minHeight &&
            theirHeight <= myPrefs.maxHeight),
      },
      {
        label: "Marital Status",
        value: formatPrefValue(myPrefs.maritalStatus),
        matched: matchField("maritalStatus"),
      },
      {
        label: "Mother Tongue",
        value: formatPrefValue(myPrefs.motherTongue),
        matched: matchField("motherTongue"),
      },
      {
        label: "State Living In",
        value: formatPrefValue(myPrefs.state),
        matched: matchField("state"),
      },
      {
        label: "City / District",
        value: formatPrefValue(myPrefs.city),
        matched: matchField("city"),
      },
      {
        label: "Qualification",
        value: formatPrefValue(myPrefs.qualification),
        matched: matchField("qualification"),
      },
      {
        label: "Religion",
        value: formatPrefValue(myPrefs.religion),
        matched: matchField("religion"),
      },
      {
        label: "Community",
        value: formatPrefValue(myPrefs.community),
        matched: matchField("community"),
      },
      {
        label: "Profession",
        value: formatPrefValue(myPrefs.profession),
        matched: matchField("profession"),
      },
      {
        label: "Annual Income",
        value: formatPrefValue(myPrefs.annualIncome),
        matched: matchField("annualIncome"),
      },
      {
        label: "Working In",
        value: formatPrefValue(myPrefs.workingIn),
        matched: matchField("workingIn"),
      },
      {
        label: "Diet",
        value: formatPrefValue(myPrefs.diet),
        matched: matchField("diet"),
      },
      {
        label: "Family Type",
        value: formatPrefValue(myPrefs.familyType),
        matched: matchField("familyType"),
      },
    ];
  };

  const preferences = useMemo(() => {
    if (profile && profile.profile && profileData && profileData.preferences) {
      return generatePreferences(profile, profileData);
    }
    return [];
  }, [profile, profileData]);

  useEffect(() => {
    const fetchUserData = async () => {
      const uid = await getUserIdFromToken();
      setUserId(uid);
      dispatch(fetchShortlist(uid));
      dispatch(fetchRequests(uid));
      dispatch(fetchActivityData(uid));
    };
    fetchUserData();
  }, [dispatch]);

  const refetchRequests = () => {
    if (!userId) return;
    dispatch(fetchRequests(userId));
  };

  useEffect(() => {
    if (userId) {
      dispatch(fetchRequests(userId));
    }
  }, [dispatch, userId]);

  const handleShortlist = async (profileId) => {
    if (!userId) return;
    setShortlistLoadingIds((ids) => [...ids, profileId]);
    try {
      await dispatch(
        addShortlist({ userId, shortlistedProfileId: profileId }),
      ).unwrap();
      await dispatch(fetchShortlist(userId));
    } catch (err) {
      console.error(err);
    } finally {
      setShortlistLoadingIds((ids) => ids.filter((id) => id !== profileId));
    }
  };

  const handleRemoveShortlist = async (profileId) => {
    if (!userId || !Array.isArray(shortlisted)) return;
    const shortlistItem = shortlisted.find(
      (s) => s?.shortlistedProfileId === profileId,
    );
    if (!shortlistItem || !shortlistItem._id) return;
    setShortlistLoadingIds((ids) => [...ids, profileId]);
    try {
      await dispatch(removeShortlist(shortlistItem._id)).unwrap();
      await dispatch(fetchShortlist(userId));
    } catch (err) {
      console.error(err);
    } finally {
      setShortlistLoadingIds((ids) => ids.filter((id) => id !== profileId));
    }
  };

  const handleSendRequest = async () => {
    try {
      setRequestLoading((prev) => ({ ...prev, [profileId]: true }));
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${server}/requests/SendRequest`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ senderId: userId, receiverId: profileId }),
      });
      if (res.ok) {
        await refetchRequests();
      }
    } catch (err) {
      console.error("Send Request Error:", err);
    } finally {
      setRequestLoading((prev) => ({ ...prev, [profileId]: false }));
    }
  };

  const handleCancelRequest = async () => {
    try {
      setRequestLoading((prev) => ({ ...prev, [profileId]: true }));
      const token = localStorage.getItem("authToken");
      const res = await fetch(
        `${server}/requests/CancelRequest?userId=${userId}&receiverId=${profileId}`,
        {
          method: "DELETE",
          headers: { Authorization: token },
        },
      );
      if (res.ok) {
        await refetchRequests();
      }
    } catch (err) {
      console.error("Cancel Request Error:", err);
    } finally {
      setRequestLoading((prev) => ({ ...prev, [profileId]: false }));
    }
  };

  const handleAcceptRequest = async () => {
    try {
      setRequestLoading((prev) => ({ ...prev, [profileId]: true }));
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${server}/requests/AcceptRequest`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ senderId: profileId, receiverId: userId }),
      });
      if (res.ok) {
        await refetchRequests();
      }
    } catch (err) {
      console.error("Accept Request Error:", err);
    } finally {
      setRequestLoading((prev) => ({ ...prev, [profileId]: false }));
    }
  };

  const isShortlisted = shortlisted.some(
    (s) => s.shortlistedProfileId === profileId,
  );
  const isLoading = shortlistLoadingIds.includes(profileId);
  const hasSentRequest = sentRequests.some((r) => r.receiverId === profileId);
  const hasReceivedRequest = receivedRequests.some(
    (r) => r.senderId === profileId,
  );
  const isRequestLoading = requestLoading[profileId] || false;
  const acceptedRequest = acceptedRequests.some((r) => r._id === profileId);

  return (
    <>
      <Header />

      <section>
        <div className="container pt-3">
          <div className="row g-3">
            <div className="col-lg-8 col-12 mb-5">
              {screenLoading && (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: "400px" }}
                >
                  <div
                    className="spinner-border text-danger"
                    role="status"
                  ></div>
                </div>
              )}

              {/* Free Plan Limit Message */}
              {!screenLoading && canVisit === false && (
                <div className="bg-white p-4 rounded-4 text-center shadow">
                  <img
                    src="/assets/images/logo.png"
                    alt="Logo"
                    className="img-fluid mb-3"
                    style={{ maxWidth: "100px" }}
                  />

                  <p className="fs-5 fst-italic fw-semibold lh-sm">
                    You have visited 10 profiles <br />
                    and reached the limit of the{" "}
                    <span className="text-danger">Free Plan</span>
                  </p>

                  <p className="text-secondary mt-2">
                    Please upgrade now to continue viewing new profiles and
                    unlock all premium features!
                  </p>

                  <div className="mt-4">
                    <button
                      className="btn bg-red-primary text-white px-6 py-2 rounded-pill fw-normal"
                      onClick={() => navigate("/upgrade")}
                    >
                      Get Membership Plan
                    </button>
                    <div className="text-secondary small mt-2">Upgrade Now</div>
                  </div>
                </div>
              )}

              {!screenLoading && canVisit !== false && (
                <div>
                  <div>
                    <div>
                      <div className="position-relative d-flex">
                        {/* ================= PROFILE IMAGES ================= */}
                        {profileData?.images?.length > 0 &&
                        profileData?.images[0]?.docUrl ? (
                          <>
                            <img
                              src={profileData.images[0].docUrl}
                              className="w-100 rounded-4 shadow position-relative z-1 bg-transparent object-fit-contain"
                              height="400"
                              alt=""
                            />
                            <img
                              src={profileData.images[0].docUrl}
                              className="w-100 rounded-4 position-absolute top-0 start-0 z-0 object-fit-cover"
                              height="400"
                              alt=""
                            />
                          </>
                        ) : (
                          <img
                            src="/assets/images/default-profile.jpg"
                            className="w-100 rounded-4 shadow z-1 object-fit-cover"
                            height="400"
                            alt=""
                          />
                        )}

                        {/* ================= IMAGE COUNT BUTTON ================= */}
                        <div className="position-absolute w-100 mt-1 pt-2 z-1 text-end">
                          {profileData?.images?.length > 0 && (
                            <Link
                              to="/images"
                              state={{ profileId: profileData?._id }}
                              className="me-3 px-3 py-2 rounded-pill d-inline-flex align-items-center border-0"
                              style={{ background: "rgba(0,0,0,0.36)" }}
                            >
                              <i className="bi bi-camera text-white"></i>
                            </Link>
                          )}
                        </div>

                        {/* ================= BOTTOM GRADIENT ================= */}
                        <div
                          className="position-absolute bottom-0 w-100 pt-5 pb-3 rounded-bottom-4 z-1"
                          style={{
                            background:
                              "linear-gradient(to top, rgba(0,0,0,0.95), transparent)",
                          }}
                        >
                          <div className="mb-3 mx-3">
                            <div className="d-flex align-items-center mb-2">
                              <h4 className="text-white fw-medium text-capitalize mb-0">
                                {profileData?.name},{" "}
                                {Math.floor(
                                  (Date.now() - profileData?.profile?.dob) /
                                    3.15576e10,
                                )}
                              </h4>
                              <img
                                src="/assets/images/verify.png"
                                className="ms-2 align-self-center"
                                style={{ width: 20, height: 20 }}
                                alt=""
                              />
                            </div>

                            <div className="fs-5 lh-sm text-white text-capitalize">
                              {profileData?.profile?.maritalStatus} ·{" "}
                              {profileData?.profile?.height} ·{" "}
                              {profileData?.profile?.city}
                            </div>

                            <div className="fs-5 lh-sm text-white text-capitalize">
                              {profileData?.profile?.profession ||
                                "No Profession"}{" "}
                              ·{" "}
                              {profileData?.profile?.annualIncome ||
                                "No Income"}
                            </div>

                            <div className="fs-5 lh-sm text-white text-capitalize">
                              {profileData?.profile?.religion} ·{" "}
                              {profileData?.profile?.community}
                            </div>
                          </div>

                          {/* ================= PROFILE MANAGED BY ================= */}
                          <div
                            className="py-1 px-3 mb-3"
                            style={{
                              background:
                                "linear-gradient(to right, #dcdcdc, #bcb8b8, transparent)",
                            }}
                          >
                            <span className="small fst-italic fw-medium text-dark text-capitalize">
                              Profile managed by {profileData?.profileFor}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ================= BASIC DETAILS CARD ================= */}
                    <div className="mt-2 mb-2">
                      <div className="bg-white rounded-4 shadow mt-n6 pt-6">
                        <div className="mx-4 rounded-4 bg-light border-top py-3 mt-3">
                          <div className="container">
                            <div className="d-flex justify-content-between align-items-center mx-lg-4">
                              {/* Left Text */}
                              <div className="me-3">
                                <h4 className="lh-sm text-dark fst-italic mb-0">
                                  Like this Profile?
                                </h4>
                                <p className="lh-sm m-0">
                                  Send Request / Shortlist this profile.
                                </p>
                              </div>

                              {/* Buttons */}
                              <div className="d-flex align-items-center">
                                {/* SHORTLIST */}
                                <span
                                  className="d-flex flex-column align-items-center me-lg-5 me-4 cursor-pointer"
                                  disabled={isLoading}
                                  onClick={() => {
                                    if (isShortlisted) {
                                      handleRemoveShortlist(profileId);
                                    } else {
                                      handleShortlist(profileId);
                                    }
                                  }}
                                >
                                  <div className="icon-shape icon-lg rounded-circle d-flex justify-content-center align-items-center bg-info">
                                    {isLoading ? (
                                      <div
                                        className="spinner-border spinner-border-sm text-white"
                                        role="status"
                                      ></div>
                                    ) : (
                                      <i
                                        className={`bi ${
                                          isShortlisted
                                            ? "bi-star-fill"
                                            : "bi-star"
                                        }`}
                                        style={{
                                          color: "#ffffff",
                                          fontSize: "22px",
                                        }}
                                      ></i>
                                    )}
                                  </div>
                                  <span className="fs-6 fw-medium text-dark mt-1 text-center text-capitalize">
                                    {isShortlisted
                                      ? "Shortlisted"
                                      : "Shortlist"}
                                  </span>
                                </span>

                                {/* REQUEST */}
                                <span
                                  className="d-flex flex-column align-items-center me-3 cursor-pointer"
                                  disabled={isRequestLoading}
                                  onClick={() => {
                                    if (isRequestLoading) return;

                                    if (acceptedRequest) {
                                      navigate("chat", {
                                        profileId: profileData?._id,
                                      });
                                    } else if (hasReceivedRequest) {
                                      handleAcceptRequest();
                                    } else if (hasSentRequest) {
                                      handleCancelRequest();
                                    } else {
                                      handleSendRequest();
                                    }
                                  }}
                                >
                                  <div className="icon-shape icon-lg rounded-circle d-flex justify-content-center align-items-center bg-danger">
                                    {isRequestLoading ? (
                                      <div
                                        className="spinner-border spinner-border-sm text-white"
                                        role="status"
                                      ></div>
                                    ) : acceptedRequest ? (
                                      <i
                                        className="bi bi-chat"
                                        style={{
                                          color: "#ffffff",
                                          fontSize: "22px",
                                        }}
                                      ></i>
                                    ) : hasReceivedRequest ? (
                                      <i
                                        className="bi bi-check-circle"
                                        style={{
                                          color: "#ffffff",
                                          fontSize: "22px",
                                        }}
                                      ></i>
                                    ) : hasSentRequest ? (
                                      <i
                                        className="bi bi-x-lg"
                                        style={{
                                          color: "#ffffff",
                                          fontSize: "22px",
                                        }}
                                      ></i>
                                    ) : (
                                      <i
                                        className="bi bi-envelope"
                                        style={{
                                          color: "#ffffff",
                                          fontSize: "22px",
                                        }}
                                      ></i>
                                    )}
                                  </div>
                                  <span className="fs-6 fw-medium text-dark mt-1 text-center text-capitalize">
                                    {acceptedRequest
                                      ? "Chat"
                                      : hasReceivedRequest
                                        ? "Accept"
                                        : hasSentRequest
                                          ? "Cancel"
                                          : "Request"}
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <h4 className="pt-4 mx-4 lh-sm m-0 text-dark fw-normal">
                          Basic Details
                        </h4>
                        <p className="mx-4 lh-sm">
                          Profile related basic details.
                        </p>

                        {/* ===== BADGES ROW 1 ===== */}
                        <div className="px-4 pt-2 mb-1 d-flex flex-wrap gap-1">
                          <span className="px-3 py-2 fs-5 border rounded-pill bg-white text-dark small">
                            <i className="bi bi-hashtag me-1 text-purple"></i>
                            ID — {profileData?.userId}
                          </span>

                          <span className="px-3 py-2 fs-5 border rounded-pill bg-white text-dark small">
                            {Math.floor(
                              (Date.now() - profileData?.profile?.dob) /
                                3.15576e10,
                            )}{" "}
                            Yrs
                          </span>
                        </div>

                        {/* ===== BADGES ROW 2 ===== */}
                        <div className="px-4 pb-3 mb-1 d-flex flex-wrap gap-2">
                          <span className="px-3 py-2 fs-5 border rounded-pill bg-white text-dark small">
                            <i className="bi bi-arrows-v me-1 text-purple"></i>
                            {profileData?.profile?.height}
                          </span>
                        </div>

                        {/* ================= DETAILS LIST ================= */}
                        <div className="px-4 pb-4">
                          {/* Birth Date */}
                          <div className="mb-2 d-flex align-items-center">
                            <div className="bg-primary rounded-pill d-flex align-items-center justify-content-center me-3  icon-shape icon-md">
                              <i className="bi bi-calendar text-white"></i>
                            </div>
                            <div>
                              <div className="fs-5 lh-sm text-secondary">
                                Birth Date
                              </div>
                              <div className="fs-5 lh-sm text-dark">
                                Born on{" "}
                                {profileData?.profile?.dob
                                  ? new Date(
                                      profileData.profile.dob,
                                    ).toLocaleDateString("en-GB")
                                  : ""}
                              </div>
                            </div>
                          </div>

                          {/* Marital Status */}
                          <div className="mb-2 d-flex align-items-center">
                            <div className="bg-primary rounded-pill d-flex align-items-center justify-content-center me-3  icon-shape icon-md">
                              <i className="bi bi-heart text-white"></i>
                            </div>
                            <div>
                              <div className="fs-5 lh-sm text-secondary">
                                Marital Status
                              </div>
                              <div className="fs-5 lh-sm text-dark">
                                {profileData?.profile?.maritalStatus}
                              </div>
                            </div>
                          </div>

                          {/* Lives In */}
                          <div className="mb-2 d-flex align-items-center">
                            <div className="bg-primary rounded-pill d-flex align-items-center justify-content-center me-3  icon-shape icon-md">
                              <i className="bi bi-geo-alt text-white"></i>
                            </div>
                            <div>
                              <div className="fs-5 lh-sm text-secondary">
                                Lives In
                              </div>
                              <div className="fs-5 lh-sm text-dark">
                                Lives in {profileData?.profile?.city},{" "}
                                {profileData?.profile?.state} - India
                              </div>
                            </div>
                          </div>

                          {/* Religion */}
                          <div className="mb-2 d-flex align-items-center">
                            <div className="bg-primary rounded-pill d-flex align-items-center justify-content-center me-3  icon-shape icon-md">
                              <i className="bi bi-book text-white"></i>
                            </div>
                            <div>
                              <div className="fs-5 lh-sm text-secondary">
                                Religion & Community
                              </div>
                              <div className="fs-5 lh-sm text-dark">
                                {profileData?.profile?.religion},{" "}
                                {profileData?.profile?.community}
                              </div>
                            </div>
                          </div>

                          {/* Mother Tongue */}
                          <div className="mb-2 d-flex align-items-center">
                            <div className="bg-primary rounded-pill d-flex align-items-center justify-content-center me-3  icon-shape icon-md">
                              <i className="bi bi-person text-white"></i>
                            </div>
                            <div>
                              <div className="fs-5 lh-sm text-secondary">
                                Mother Tongue
                              </div>
                              <div className="fs-5 lh-sm text-dark">
                                {profileData?.profile?.motherTongue}
                              </div>
                            </div>
                          </div>

                          {/* Diet */}
                          <div className="mb-2 d-flex align-items-center">
                            <div className="bg-primary rounded-pill d-flex align-items-center justify-content-center me-3  icon-shape icon-md">
                              <i className="bi bi-cup-straw text-white"></i>
                            </div>
                            <div>
                              <div className="fs-5 lh-sm text-secondary">
                                Diet
                              </div>
                              <div className="fs-5 lh-sm text-dark">
                                {profileData?.profile?.diet}
                              </div>
                            </div>
                          </div>

                          {/* ================= GO PREMIUM ================= */}
                          <div className="d-flex justify-content-center">
                            <Link
                              to="/upgrade"
                              className="mt-3 bg-black text-white px-5 py-2 rounded-pill fw-normal shadow border-0"
                            >
                              Go Premium Now
                            </Link>
                          </div>

                          <div className="text-center text-secondary small mt-1">
                            Upgrade
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 mb-2 shadow border-top border-3 border-primary bg-white rounded-4">
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4 className="pt-4 mx-4 lh-sm m-0 text-dark fw-normal">
                          Contact Details
                        </h4>
                        <p className="mx-4 lh-sm">
                          Verified details of contact person
                        </p>
                      </div>
                      <i className="bi bi-lightning-fill text-danger fs-4 me-4 pt-3"></i>
                    </div>

                    {/* Body */}
                    <div className="px-4 pt-3 pb-4">
                      {/* Phone */}
                      <div className="d-flex align-items-center mb-2">
                        <div className="bg-success rounded-pill d-flex align-items-center justify-content-center  icon-shape icon-md">
                          <i className="bi bi-telephone text-white"></i>
                        </div>

                        <div className="ms-3">
                          <p className="m-0 lh-sm text-secondary">
                            Contact No.
                          </p>
                          <p className="mb-0 text-dark">
                            +91{" "}
                            {profileData?.mobile
                              ? isUnlocked
                                ? profileData.mobile
                                : getMaskedMobile(profileData.mobile)
                              : ""}
                          </p>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="d-flex align-items-center mb-2">
                        <div className="bg-success rounded-pill d-flex align-items-center justify-content-center  icon-shape icon-md">
                          <i className="bi bi-envelope text-white"></i>
                        </div>

                        <div className="ms-3">
                          <p className="m-0 lh-sm text-secondary">Email ID</p>
                          <p className="mb-0 text-dark">
                            {profileData?.email
                              ? isUnlocked
                                ? profileData.email
                                : getMaskedEmail(profileData.email)
                              : "Not Mentioned"}
                          </p>
                        </div>
                      </div>

                      {/* Unlock Button — only show after check is complete */}
                      {unlockCheckLoading ? (
                        <div className="text-center mt-3">
                          <div
                            className="spinner-border spinner-border-sm text-secondary"
                            role="status"
                          ></div>
                        </div>
                      ) : (
                        !isUnlocked && (
                          <div className="text-center mt-3 d-flex flex-column align-items-center justify-content-center">
                            <button
                              className="btn btn-dark rounded-pill px-5 py-2 fw-normal shadow d-flex align-items-center justify-content-center gap-2"
                              onClick={handleUnlockProfile}
                              disabled={unlockLoading}
                            >
                              {unlockLoading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm text-white"
                                    role="status"
                                  ></span>
                                  Unlocking Contact Details
                                </>
                              ) : (
                                "Unlock Contact Details"
                              )}
                            </button>
                            {!unlockLoading && (
                              <div className="text-secondary small mt-1">
                                Unlock Now
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  <div className="">
                    <div className="shadow bg-white rounded-4 h-100">
                      <h4 className="pt-4 mx-4 lh-sm m-0 text-dark fw-normal">
                        Family Details
                      </h4>
                      <p className="mx-4 lh-sm">
                        Family related details mentioned.
                      </p>

                      <div className="px-4 pt-2">
                        <div className="d-flex align-items-center mb-2">
                          <div className="bg-info rounded-pill d-flex justify-content-center align-items-center  icon-shape icon-md">
                            <i className="bi bi-people text-white"></i>
                          </div>

                          <div className="ms-3 lh-sm">
                            <small className="text-secondary">
                              Parent's Details
                            </small>
                            <p className="mb-0 text-dark">
                              Father is {profileData?.profile?.fatherStatus}
                              <br />
                              Mother is {profileData?.profile?.motherStatus}
                            </p>
                          </div>
                        </div>

                        <div className="d-flex align-items-center mb-2 pt-1">
                          <div className="bg-info rounded-pill d-flex justify-content-center align-items-center  icon-shape icon-md">
                            <i className="bi bi-people text-white"></i>
                          </div>

                          <div className="ms-3 lh-sm">
                            <small className="text-secondary">
                              No. Of Siblings
                            </small>
                            <p className="mb-0 text-dark">
                              {profileData?.profile?.noOfBrothers || 0} Brother{" "}
                              <br />
                              {profileData?.profile?.noOfSisters || 0} Sister
                            </p>
                          </div>
                        </div>

                        <h4 className="pt-4 lh-sm m-0 text-dark fw-normal">
                          Career & Education
                        </h4>
                        <p className="lh-sm">
                          Basic details of career & education.
                        </p>

                        <div className="py-3">
                          {/* Profession */}
                          <div className="d-flex align-items-center mb-2">
                            <div className="bg-secondary rounded-pill d-flex justify-content-center align-items-center  icon-shape icon-md">
                              <i className="bi bi-briefcase text-white"></i>
                            </div>
                            <div className="ms-3 lh-sm">
                              <small className="text-secondary">
                                Profession
                              </small>
                              <p className="mb-0 text-dark">
                                {profileData?.profile?.profession ||
                                  "No Profession"}
                              </p>
                            </div>
                          </div>

                          {/* Qualification */}
                          <div className="d-flex align-items-center mb-2">
                            <div className="bg-secondary rounded-pill d-flex justify-content-center align-items-center  icon-shape icon-md">
                              <i className="bi bi-book text-white"></i>
                            </div>
                            <div className="ms-3 lh-sm">
                              <small className="text-secondary">
                                Qualification
                              </small>
                              <p className="mb-0 text-dark">
                                {profileData?.profile?.qualification ||
                                  "No Qualification"}
                              </p>
                            </div>
                          </div>

                          {/* Working In */}
                          <div className="d-flex align-items-center mb-2">
                            <div className="bg-secondary rounded-pill d-flex justify-content-center align-items-center  icon-shape icon-md">
                              <i className="bi bi-building text-white"></i>
                            </div>
                            <div className="ms-3 lh-sm">
                              <small className="text-secondary">
                                Working In
                              </small>
                              <p className="mb-0 text-dark">
                                {profileData?.profile?.workingIn ||
                                  "Not Working"}
                              </p>
                            </div>
                          </div>

                          {/* Company Name */}
                          <div className="d-flex align-items-center mb-2">
                            <div className="bg-secondary rounded-pill d-flex justify-content-center align-items-center  icon-shape icon-md">
                              <i className="bi bi-people text-white"></i>
                            </div>
                            <div className="ms-3 lh-sm">
                              <small className="text-secondary">
                                Company Name
                              </small>
                              <p className="mb-0 text-dark">
                                {profileData?.profile?.companyName ||
                                  "Not Mentioned"}
                              </p>
                            </div>
                          </div>

                          {/* Annual Income */}
                          <div className="d-flex align-items-center mb-2">
                            <div className="bg-secondary rounded-pill d-flex justify-content-center align-items-center  icon-shape icon-md">
                              <i className="bi bi-wallet2 text-white"></i>
                            </div>
                            <div className="ms-3 lh-sm">
                              <small className="text-secondary">
                                Annual Income
                              </small>
                              <p className="mb-0 text-dark">
                                {profileData?.profile?.annualIncome ||
                                  "No Income"}
                              </p>
                            </div>
                          </div>

                          {/* College / School Name */}
                          <div className="d-flex align-items-center mb-2">
                            <div className="bg-secondary rounded-pill d-flex justify-content-center align-items-center  icon-shape icon-md">
                              <i className="bi bi-bank2 text-white"></i>
                            </div>
                            <div className="ms-3 lh-sm">
                              <small className="text-secondary">
                                College / School Name
                              </small>
                              <p className="mb-0 text-dark">
                                {profileData?.profile?.collegeName ||
                                  "Not Mentioned"}
                              </p>
                            </div>
                          </div>

                          {/* Go Premium Button */}
                          <div className="d-flex justify-content-center mt-3">
                            <Link
                              to="/upgrade"
                              className="btn btn-dark px-4 py-2 shadow rounded-pill"
                            >
                              Go Premium Now
                            </Link>
                          </div>
                          <p className="text-secondary fs-7 mt-1 text-center">
                            Unlock Details
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-center mt-3 text-white bg-red-primary pt-5 rounded-top-4 shadow lh-sm">
                    <h4 className="m-0 lh-1 text-white">
                      You & {profile?.gender === "Male" ? "Her" : "His"}
                    </h4>
                    <p className="mb-3 text-white">Based on your Preferences</p>

                    <div className="d-flex justify-content-center align-items-center mb-n5">
                      <img
                        src={
                          profile?.images?.[0]?.docUrl ||
                          "/assets/images/profile.png"
                        }
                        className="rounded-circle border border-secondary object-fit-cover"
                        width="70"
                        height="70"
                        alt=""
                      />

                      <img
                        src={
                          profileData?.images?.[0]?.docUrl ||
                          "/assets/images/profile.png"
                        }
                        className="rounded-circle border border-secondary ms-n3 object-fit-cover"
                        width="70"
                        height="70"
                        alt=""
                      />
                    </div>
                  </div>

                  <div className="bg-white p-4 pt-7 rounded-bottom shadow border-bottom border-3 border-primary">
                    {preferences.map((item, index) => (
                      <div
                        key={index}
                        className="d-flex justify-content-between align-items-center mb-2"
                      >
                        <div>
                          <p className="fs-5 lh-1 text-dark mb-0 fw-medium">
                            {item.label}
                          </p>
                          <p className="text-secondary m-0">{item.value}</p>
                        </div>

                        <div
                          className={`rounded-circle d-flex align-items-center justify-content-center  icon-shape icon-md ${
                            item.matched ? "bg-dark" : "bg-light"
                          }`}
                        >
                          {item.matched ? (
                            <i className="bi bi-check text-white"></i>
                          ) : (
                            <i className="bi bi-x-lg text-dark"></i>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Right />
          </div>
        </div>
      </section>
    </>
  );
}

export default Profile;
